/* Local quantity edits. Shopify is contacted only when Checkout/Clear cart is pressed. */
(() => {
  'use strict';
  const quantityOf = group => group ? group.lines.reduce((n, line) => n + line.quantity, 0) : 0;
  function groupCart(items) {
    const groups = new Map();
    for (const item of items) {
      const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
      const id = JSON.stringify([item.variant_id, properties, item.selling_plan_allocation?.selling_plan?.id || 0]);
      if (!groups.has(id)) groups.set(id, { id, lines: [] });
      groups.get(id).lines.push({ key: item.key, quantity: item.quantity });
    }
    return groups;
  }
  function planUpdate(group, target) {
    const total = quantityOf(group);
    if (!Number.isSafeInteger(target) || target < 0) throw new Error('Invalid quantity.');
    if (target === total) return null;
    if (!group) throw new Error('An item changed in your cart. Please reload and review it.');
    if (target > total) return { endpoint: 'change', body: { id: group.lines[0].key, quantity: group.lines[0].quantity + target - total } };
    let remaining = target;
    const updates = {};
    group.lines.forEach(line => {
      const keep = Math.min(line.quantity, remaining);
      remaining -= keep;
      if (keep !== line.quantity) updates[line.key] = keep;
    });
    return { endpoint: 'update', body: { updates } };
  }
  function rebase(entries, groups, absolute = false, strict = false) {
    const result = new Map();
    for (const entry of entries) {
      if (!entry || typeof entry.id !== 'string' || !Number.isSafeInteger(entry.base) || !Number.isSafeInteger(entry.quantity) || entry.base < 0 || entry.quantity < 0) continue;
      const group = groups.get(entry.id);
      if (!group) {
        if (strict && entry.quantity > 0) throw new Error('An item was removed from your cart. Please reload and review it.');
        continue; // Never resurrect items removed elsewhere.
      }
      const base = quantityOf(group);
      const quantity = absolute || entry.quantity === 0 ? entry.quantity : Math.max(0, base + entry.quantity - entry.base);
      if (quantity !== base) result.set(entry.id, { id: entry.id, base, quantity });
    }
    return result;
  }
  async function commit(entries, initialCart, send, onProgress) {
    let cart = initialCart;
    for (const entry of entries) {
      const plan = planUpdate(groupCart(cart.items).get(entry.id), entry.quantity);
      if (plan) cart = await send(plan);
      if (quantityOf(groupCart(cart.items).get(entry.id)) !== entry.quantity) throw new Error('Shopify adjusted an item quantity. Please reload and review your cart.');
      onProgress(entry.id, cart);
    }
    return cart;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { quantityOf, groupCart, planUpdate, rebase, commit };
    return;
  }
  if (document.documentElement.dataset.quantityControlsReady) return;
  document.documentElement.dataset.quantityControlsReady = 'true';
  const root = document.querySelector('[data-auto-cart]');
  const rows = root ? Array.from(root.querySelectorAll('[data-cart-group]')) : [];
  const groups = new Map(rows.map(row => [row.dataset.groupId, { id: row.dataset.groupId, lines: JSON.parse(row.dataset.groupLines) }]));
  const form = root?.querySelector('[data-grouped-items]')?.closest('form');
  const checkout = form?.querySelector('[name="checkout"]');
  const storageKey = '3da-checkout-edits-v1-' + (root?.dataset.storageScope || '');
  let pending = new Map(), busy = false, allowCheckout = false, storageOK = true;

  function persist(committing = false) {
    try {
      if (!pending.size) sessionStorage.removeItem(storageKey);
      else sessionStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), committing, entries: [...pending.values()] }));
      storageOK = true;
    } catch { storageOK = false; }
  }
  function sync(control) {
    const input = control.querySelector('input');
    control.querySelector('[data-quantity-step="-1"]').disabled = busy || Number(input.value) <= Number(input.min || 1);
    control.querySelector('[data-quantity-step="1"]').disabled = busy || (input.max && Number(input.value) >= Number(input.max));
  }
  function paint() {
    if (!root) return;
    rows.forEach(row => {
      const input = row.querySelector('input');
      input.value = String(pending.get(row.dataset.groupId)?.quantity ?? quantityOf(groups.get(row.dataset.groupId)));
      sync(row.querySelector('[data-tap-quantity]'));
      row.querySelector('[data-remove-group]').disabled = busy || Number(input.value) === 0;
    });
    const dirty = pending.size > 0;
    root.querySelectorAll('[data-confirmed-price],[data-confirmed-total]').forEach(el => { el.hidden = dirty; });
    root.querySelectorAll('[data-pending-price],[data-pending-total]').forEach(el => { el.hidden = !dirty; });
    const count = rows.reduce((sum, row) => sum + Number(row.querySelector('input').value), 0);
    root.querySelector('[data-cart-status]').textContent = dirty ? (count ? 'Final total and discounts shown at checkout.' : 'No items selected. Press Clear cart to confirm removal.') + (storageOK ? '' : ' Keep this tab open until checkout.') : '';
    if (checkout) { checkout.disabled = busy; checkout.textContent = count ? 'Checkout' : 'Clear cart'; }
  }
  if (root) {
    try {
      const stored = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (stored && Array.isArray(stored.entries) && Date.now() - stored.at < 86400000) pending = rebase(stored.entries, groups, stored.committing);
    } catch { storageOK = false; }
    root.querySelectorAll('[data-native-cart]').forEach(native => {
      native.hidden = true;
      native.querySelectorAll('input,button').forEach(el => { el.disabled = true; });
    });
    root.querySelectorAll('[data-grouped-items]').forEach(el => { el.hidden = false; });
    root.querySelectorAll('[data-manual-update]').forEach(el => { el.hidden = true; el.disabled = true; });
    persist();
  }
  document.querySelectorAll('[data-tap-quantity]').forEach(control => {
    if (control.closest('[data-native-cart]')) return;
    control.querySelector('input').readOnly = true;
    control.querySelectorAll('button').forEach(button => { button.hidden = false; });
    control.classList.add('is-ready'); sync(control);
  });
  paint();

  function edit(row, quantity) {
    if (busy || !Number.isSafeInteger(quantity) || quantity < 0) return;
    const id = row.dataset.groupId, base = quantityOf(groups.get(id));
    if (quantity === base) pending.delete(id); else pending.set(id, { id, base, quantity });
    persist(); paint();
  }
  function step(control, direction) {
    if (busy) return;
    const input = control.querySelector('input');
    const quantity = Math.min(input.max ? Number(input.max) : Infinity, Math.max(Number(input.min || 1), Number(input.value) + direction * Number(input.step || 1)));
    const row = control.closest('[data-cart-group]');
    if (row) edit(row, quantity); else { input.value = String(quantity); sync(control); }
  }
  document.addEventListener('click', event => {
    const remove = event.target.closest('[data-remove-group]');
    if (remove) { edit(remove.closest('[data-cart-group]'), 0); return; }
    const button = event.target.closest('[data-quantity-step]');
    if (button) { event.preventDefault(); step(button.closest('[data-tap-quantity]'), Number(button.dataset.quantityStep)); }
  });
  document.addEventListener('keydown', event => {
    if (event.target.matches('[data-tap-quantity] input') && ['ArrowUp','ArrowDown'].includes(event.key)) {
      event.preventDefault(); step(event.target.closest('[data-tap-quantity]'), event.key === 'ArrowUp' ? 1 : -1);
    }
  });
  async function request(url, body) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
      const cart = await response.json();
      if (!response.ok) throw new Error(typeof cart.description === 'string' ? cart.description : 'Shopify could not confirm your cart.');
      return cart;
    } finally { clearTimeout(timer); }
  }
  document.addEventListener('submit', async event => {
    if (event.target !== form || allowCheckout) return;
    if (!pending.size && !busy) return; // The standard Shopify checkout form handles an unchanged cart.
    event.preventDefault();
    if (busy) return;
    busy = true; paint();
    root.querySelectorAll('.cart-upsell button').forEach(button => { button.disabled = true; });
    checkout.textContent = 'Saving your cart…';
    const status = root.querySelector('[data-cart-status]');
    status.textContent = 'Saving your quantities before checkout…';
    let savingAbsoluteTargets = false;
    try {
      const fresh = await request(root.dataset.cartUrl + '.js');
      pending = rebase([...pending.values()], groupCart(fresh.items), false, true);
      savingAbsoluteTargets = true;
      persist(true);
      const finalCart = await commit([...pending.values()], fresh,
        plan => request(plan.endpoint === 'change' ? root.dataset.changeUrl : root.dataset.updateUrl, plan.body),
        id => { pending.delete(id); persist(true); });
      pending.clear(); persist();
      if (!finalCart.item_count) { window.location.assign(root.dataset.cartUrl); return; }
      allowCheckout = true;
      checkout.disabled = false;
      checkout.textContent = 'Opening checkout…';
      form.requestSubmit(checkout);
    } catch (error) {
      // Keep any unconfirmed absolute targets for review. Never retry an uncertain write automatically.
      persist(savingAbsoluteTargets);
      status.textContent = (error.name === 'AbortError' ? 'The save took too long.' : error.message) + ' Reload to review your cart before checkout. ';
      const link = document.createElement('a');
      link.href = root.dataset.cartUrl; link.dataset.cartRecovery = 'true'; link.textContent = 'Reload cart';
      status.append(link); link.focus();
      checkout.textContent = 'Review cart to continue';
    }
  }, true);
  document.addEventListener('click', event => {
    if (busy && event.target.closest('[data-auto-cart] a:not([data-cart-recovery])')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  window.addEventListener('pageshow', event => { if (event.persisted && root) window.location.reload(); });
})();
